// 客户端 is_cn 探测：唯一真源。
// 不再依赖 /api/site 返回的 is_cn，改为主动探测外网（Google favicon）连通性。
// 可达（外网畅通）→ 海外 → is_cn=false；不可达 → 境内 → is_cn=true。
//
// 取值优先级（同步）：内存（本次探测结果）> localStorage（上次探测）> 默认 cn。
// 这样首屏立即有值可用，不阻塞内容请求；探测完成后若变化，订阅者自行重载。

import { cacheGet, cacheSet } from './cache'

const STORAGE_KEY = 'is_cn'      // 经 cache.js 前缀 → crirp_is_cn
const DEFAULT_IS_CN = true       // 探测未完成且无存储时，按境内处理
const PROBE_URL = 'https://www.google.com/favicon.ico'
const PROBE_TIMEOUT = 3000

// 内存全局：null 表示本次会话尚未探测完成
let _isCn = null
// 探测 promise（幂等，整页只跑一次）
let _probe = null
// 变化订阅者
const _listeners = new Set()

function readStored() {
  const v = cacheGet(STORAGE_KEY, null)
  return v === true || v === false ? v : null
}

// 同步返回当前最佳已知值：内存 > 存储 > 默认 cn
export function getIsCn() {
  if (_isCn !== null) return _isCn
  const stored = readStored()
  return stored !== null ? stored : DEFAULT_IS_CN
}

// r 分区参数统一入口
export function regionR() {
  return getIsCn() ? 'cn' : 'os'
}

// 探测外网连通性：可达返回 true。no-cors 无法读响应，但能感知网络通断 / 是否被阻断。
async function checkGlobalNetwork(timeout = PROBE_TIMEOUT) {
  const testUrl = `${PROBE_URL}?t=${Date.now()}`
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeout)
  try {
    await fetch(testUrl, { mode: 'no-cors', signal: controller.signal })
    clearTimeout(timer)
    return true
  } catch (e) {
    clearTimeout(timer)
    return false
  }
}

// 启动一次探测（幂等）。完成后写内存 + 存储，值变化则通知订阅者。
export function probeRegion() {
  if (_probe) return _probe
  _probe = (async () => {
    const reachable = await checkGlobalNetwork()
    const prev = getIsCn()
    _isCn = !reachable            // 可达 → 海外 → 非 cn
    cacheSet(STORAGE_KEY, _isCn)
    if (_isCn !== prev) {
      _listeners.forEach((fn) => {
        try { fn(_isCn, prev) } catch (e) { /* 单个订阅者异常不影响其它 */ }
      })
    }
    return _isCn
  })()
  return _probe
}

// 订阅 is_cn 变化，返回取消订阅函数。
export function onRegionChange(fn) {
  _listeners.add(fn)
  return () => { _listeners.delete(fn) }
}

// 本次会话探测是否已完成（_isCn 已是确定值，而非存储/默认的猜测）。
// 用于「仅海外可见」内容：探测未完成时拿到 451 不应立即显示「无法访问」，应等裁决。
export function isRegionResolved() {
  return _isCn !== null
}

// 等待探测完成（幂等：未启动则启动）。resolve 后 isRegionResolved() 必为 true。
export function whenRegionReady() {
  return probeRegion()
}
